import { Injectable } from '@angular/core';
import DOMPurify from 'dompurify';

/**
 * Service for sanitizing HTML content to prevent XSS attacks.
 * Uses DOMPurify library for robust HTML sanitization.
 */
@Injectable({
  providedIn: 'root',
})
export class HtmlSanitizerService {
  /**
   * Sanitizes HTML content using DOMPurify to prevent XSS attacks.
   * Allows safe HTML tags and attributes while removing dangerous code.
   * 
   * @param dirtyHtml - The potentially unsafe HTML content
   * @returns Sanitized HTML safe for rendering
   */
  sanitize(dirtyHtml: string): string {
    if (!dirtyHtml) {
      return '';
    }

    // Configure DOMPurify to allow safe HTML elements
    const config = {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 's', 'sub', 'sup',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'a', 'img',
        'blockquote', 'pre', 'code',
        'div', 'span',
      ],
      ALLOWED_ATTR: [
        'href', 'target', 'rel',
        'src', 'alt', 'width', 'height',
        'class', 'id', 'style',
        'colspan', 'rowspan',
      ],
      ALLOW_DATA_ATTR: false, // Prevent data-* attributes which could be used for XSS
      ALLOW_UNKNOWN_PROTOCOLS: false, // Only allow http:, https:, mailto:, etc.
    };

    // Sanitize and return
    return DOMPurify.sanitize(dirtyHtml, config);
  }

  /**
   * Sanitizes HTML content with strict configuration (minimal allowed tags).
   * Use for user-generated content where only basic formatting is needed.
   * 
   * @param dirtyHtml - The potentially unsafe HTML content
   * @returns Sanitized HTML with only basic formatting tags
   */
  sanitizeStrict(dirtyHtml: string): string {
    if (!dirtyHtml) {
      return '';
    }

    const strictConfig = {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u'],
      ALLOWED_ATTR: [],
      ALLOW_DATA_ATTR: false,
    };

    return DOMPurify.sanitize(dirtyHtml, strictConfig);
  }

  /**
   * Strips all HTML tags from content, leaving only plain text.
   * Useful for preview text or search indexing.
   * 
   * @param dirtyHtml - The HTML content to strip
   * @returns Plain text without HTML tags
   */
  stripHtml(dirtyHtml: string): string {
    if (!dirtyHtml) {
      return '';
    }

    return DOMPurify.sanitize(dirtyHtml, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    });
  }
}
